import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { STRINGS } from '@/strings/strings';
import { CharacterCounter } from '../geschichte/CharacterCounter';
import {
  LEGACY_PANEL_GEOMETRY_FALLBACK,
  type PreviewPage,
  type PreviewPanelGeometry,
} from '../../types';
import { usePanelAndTextPreview, useSavePanelAndText } from '../../use-panel-and-text';

/**
 * Sebastian ruling 2026-09-17 fix-5 (items A, B, C, D, E) — combined
 * text + panel editor on the approval screen. Replaces the pre-
 * fix-5 pair (option 1 text editor + option 2 drag surface); one
 * dialog, one save, one live-preview loop.
 *
 * ═══ LAYOUT ═══════════════════════════════════════════════════════
 *
 * Vertical stack: heading → hint → image with panel outline (drag
 * body + side grips) → text label → textarea → counter → error →
 * save/cancel. Scrolls on narrow screens.
 *
 * ═══ LIVE PREVIEW (item C) ══════════════════════════════════════
 *
 * `usePanelAndTextPreview` debounces the last (text, width, top,
 * left) request by ~400 ms and hits `POST .../panel-and-text/preview`
 * (read-only). The returned drawn panel replaces the local outline;
 * an overflow response surfaces the German error under the text
 * field and disables Speichern. `endpointAvailable === false`
 * (deploy-race, 404) turns off live preview silently — the outline
 * still moves/resizes client-side, save falls back to the combined
 * or old endpoints.
 *
 * ═══ FALLBACK (item F) ══════════════════════════════════════════
 *
 * `panelGeometry` optional. When undefined the resize grips are
 * hidden AND the live-preview loop is disabled (the missing
 * envelope field signals the old backend, which lacks the preview
 * endpoint). Move + text edit still work with the pre-slice frame
 * values; save calls the combined endpoint but on 404 the caller
 * (PageChangeDialog) can fall back to old separate endpoints.
 */

const CUSTOMER_EDIT_MIN_CHARS = 10;
const HANDLE_PX = 44;

export interface PanelAndTextEditorProps {
  readonly bookOrderId: string;
  readonly sceneId: string;
  readonly initialText: string;
  readonly initialDrawnPanel: NonNullable<PreviewPage['drawnPanel']>;
  readonly rawImageUrl: string;
  readonly panelGeometry?: PreviewPanelGeometry;
  readonly onClose: () => void;
  readonly onSaved: () => void;
  /** Called when the combined endpoint returns 404 so the parent
   *  can retry through the pre-fix-5 separate endpoints (item F). */
  readonly onCombinedEndpoint404?: (text: string, width: number, left: number, top: number) => void;
}

type DragMode = 'idle' | 'move' | 'resize-left' | 'resize-right';

export function PanelAndTextEditor({
  bookOrderId,
  sceneId,
  initialText,
  initialDrawnPanel,
  rawImageUrl,
  panelGeometry,
  onClose,
  onSaved,
  onCombinedEndpoint404,
}: PanelAndTextEditorProps) {
  const geometry = panelGeometry ?? LEGACY_PANEL_GEOMETRY_FALLBACK;
  const resizeSurfaceAvailable = panelGeometry !== undefined;
  const {
    topMarginFrac,
    bottomMarginFrac,
    sideMarginFrac,
    minWidthFrac,
    maxWidthFrac,
    maxCharsScene,
    sceneTextFontFrac,
    sceneTextLeadingMult,
  } = geometry;

  // Text + panel state — starts from the envelope's initial values.
  const [text, setText] = useState(initialText);
  const [left, setLeft] = useState(initialDrawnPanel.left);
  const [top, setTop] = useState(initialDrawnPanel.top);
  const [width, setWidth] = useState(initialDrawnPanel.width);
  const [height, setHeight] = useState(initialDrawnPanel.height);
  const [writableWidth, setWritableWidth] = useState(initialDrawnPanel.writableWidth);
  const [overflow, setOverflow] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [dragMode, setDragMode] = useState<DragMode>('idle');
  const [dragWidthFrac, setDragWidthFrac] = useState<number | null>(null);

  const imageRef = useRef<HTMLDivElement | null>(null);
  const dragOffsetRef = useRef<{ x: number; y: number } | null>(null);
  const dragAnchorLeftRef = useRef<number>(0);
  const dragAnchorRightRef = useRef<number>(0);
  const [containerPx, setContainerPx] = useState(0);

  useLayoutEffect(() => {
    const el = imageRef.current;
    if (!el) return;
    const measure = () => setContainerPx(el.getBoundingClientRect().width);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // Debounced live preview.
  const preview = usePanelAndTextPreview(
    bookOrderId,
    sceneId,
    useCallback((result) => {
      if (result.ok) {
        setLeft(result.response.panel.left);
        setTop(result.response.panel.top);
        setWidth(result.response.panel.width);
        setHeight(result.response.panel.height);
        setWritableWidth(result.response.panel.writableWidth);
        setOverflow(false);
      } else if (result.endpoint404) {
        // Silent: live preview simply stops. The outline stays at
        // its last client-side geometry; save handles overflow
        // when the server rejects.
      } else {
        const code = result.error?.envelope?.error_code;
        if (code === 'scene_text_panel_overflow') {
          setOverflow(true);
        }
      }
    }, []),
  );

  const save = useSavePanelAndText(bookOrderId, sceneId);

  // Schedule a live preview after every state change that alters
  // the panel or the text. Skips when live preview is disabled.
  useEffect(() => {
    if (!preview.endpointAvailable) return;
    if (dragMode !== 'idle') return;
    preview.schedule({ text, width, left, top });
  }, [preview, text, width, left, top, dragMode]);

  // Move-body drag.
  const handleBodyPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!imageRef.current || save.saving) return;
      e.currentTarget.setPointerCapture(e.pointerId);
      const rect = imageRef.current.getBoundingClientRect();
      dragOffsetRef.current = {
        x: e.clientX - rect.left - left * rect.width,
        y: e.clientY - rect.top - top * rect.height,
      };
      setDragMode('move');
    },
    [left, top, save.saving],
  );

  const beginResize = useCallback(
    (e: React.PointerEvent<HTMLDivElement>, side: 'left' | 'right') => {
      if (!imageRef.current || save.saving) return;
      if (!resizeSurfaceAvailable) return;
      e.stopPropagation();
      e.currentTarget.setPointerCapture(e.pointerId);
      dragAnchorLeftRef.current = left;
      dragAnchorRightRef.current = left + width;
      setDragMode(side === 'left' ? 'resize-left' : 'resize-right');
      setDragWidthFrac(width);
    },
    [left, width, save.saving, resizeSurfaceAvailable],
  );

  const handleContainerPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!imageRef.current || dragMode === 'idle') return;
      const rect = imageRef.current.getBoundingClientRect();
      if (dragMode === 'move') {
        if (dragOffsetRef.current === null) return;
        const nextLeft = (e.clientX - rect.left - dragOffsetRef.current.x) / rect.width;
        const nextTop = (e.clientY - rect.top - dragOffsetRef.current.y) / rect.height;
        setLeft(Math.min(Math.max(nextLeft, sideMarginFrac), 1 - sideMarginFrac - width));
        setTop(Math.min(Math.max(nextTop, topMarginFrac), 1 - bottomMarginFrac - height));
        return;
      }
      const pointerFrac = (e.clientX - rect.left) / rect.width;
      if (dragMode === 'resize-right') {
        const requestedRight = Math.max(pointerFrac, dragAnchorLeftRef.current + minWidthFrac);
        const clampedRight = Math.min(requestedRight, 1 - sideMarginFrac);
        const nextWidth = Math.min(
          Math.max(clampedRight - dragAnchorLeftRef.current, minWidthFrac),
          maxWidthFrac,
        );
        setDragWidthFrac(nextWidth);
      } else {
        const requestedLeft = Math.min(pointerFrac, dragAnchorRightRef.current - minWidthFrac);
        const clampedLeft = Math.max(requestedLeft, sideMarginFrac);
        const nextWidth = Math.min(
          Math.max(dragAnchorRightRef.current - clampedLeft, minWidthFrac),
          maxWidthFrac,
        );
        setDragWidthFrac(nextWidth);
        setLeft(Math.max(dragAnchorRightRef.current - nextWidth, sideMarginFrac));
      }
    },
    [dragMode, sideMarginFrac, topMarginFrac, bottomMarginFrac, width, height, minWidthFrac, maxWidthFrac],
  );

  const handleContainerPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (dragMode === 'idle') return;
      if ((e.currentTarget as Element).hasPointerCapture?.(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
      dragOffsetRef.current = null;
      const finished = dragMode;
      setDragMode('idle');
      if (finished === 'resize-left' || finished === 'resize-right') {
        if (dragWidthFrac !== null) setWidth(dragWidthFrac);
        setDragWidthFrac(null);
      }
    },
    [dragMode, dragWidthFrac],
  );

  const handleSave = useCallback(async () => {
    if (overflow) return;
    setSaveError(null);
    const result = await save.save({ text, width, left, top });
    if (result.ok) {
      onSaved();
      onClose();
    } else if (result.endpoint404 && onCombinedEndpoint404) {
      onCombinedEndpoint404(text, width, left, top);
    } else {
      const envelopeMessage = result.error?.envelope?.user_message ?? null;
      const code = result.error?.envelope?.error_code;
      if (code === 'scene_text_panel_overflow') {
        setOverflow(true);
      }
      setSaveError(envelopeMessage ?? STRINGS.book.review.editorSaveError);
    }
  }, [overflow, save, text, width, left, top, onSaved, onClose, onCombinedEndpoint404]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const fontSizePx = containerPx > 0 ? sceneTextFontFrac * containerPx : 0;
  // Horizontal padding — fraction of panel WIDTH. Direct from the
  // server-returned drawn geometry.
  const paddingXFrac = width > 0 ? (width - writableWidth) / 2 / width : 0;
  // Fix-6 (Sebastian ruling 2026-09-17) — vertical padding is a
  // fraction of panel HEIGHT, not panel WIDTH. Pre-fix the same
  // `paddingXFrac` was applied to top/bottom, producing tiny
  // top/bottom gaps that made the text hug the top of the panel
  // and leave a large empty band at the bottom (Sebastian's "extra
  // empty line" finding). Doc-fraction of the padding in mm is
  // `paddingMmFrac = paddingXFrac × width` (unchanged conceptually);
  // as a fraction of height it is `paddingMmFrac / height`.
  const paddingYFrac = height > 0 ? (paddingXFrac * width) / height : 0;
  const outlineOnly = dragMode === 'resize-left' || dragMode === 'resize-right';
  const renderedWidthFrac = outlineOnly && dragWidthFrac !== null ? dragWidthFrac : width;

  const textEditorAriaLabel = useMemo(
    () => STRINGS.book.review.editorAriaLabel.replace('{n}', sceneId),
    [sceneId],
  );
  const textChanged = text !== initialText;
  const geometryChanged =
    left !== initialDrawnPanel.left ||
    top !== initialDrawnPanel.top ||
    width !== initialDrawnPanel.width;
  const saveDisabled =
    save.saving ||
    overflow ||
    (!textChanged && !geometryChanged) ||
    text.length < CUSTOMER_EDIT_MIN_CHARS;

  return (
    <div className="space-y-3 max-h-[85vh] overflow-y-auto">
      <h2 id="page-change-dialog-heading" className="text-lg font-semibold text-dream-navy pr-6">
        {STRINGS.book.review.combinedEditorHeading}
      </h2>
      <p className="text-sm text-dream-navy/70">{STRINGS.book.review.combinedEditorHint}</p>

      <div
        ref={imageRef}
        className="relative w-full overflow-hidden rounded-lg bg-dream-navy/5"
        style={{ aspectRatio: '1 / 1' }}
        onPointerMove={handleContainerPointerMove}
        onPointerUp={handleContainerPointerUp}
        onPointerCancel={handleContainerPointerUp}
      >
        <img
          src={rawImageUrl}
          alt=""
          className="absolute inset-0 h-full w-full object-contain"
          draggable={false}
        />
        {/* Margin cage */}
        <div
          aria-hidden="true"
          className="absolute border border-dream-navy/20 pointer-events-none"
          style={{
            left: `${sideMarginFrac * 100}%`,
            top: `${topMarginFrac * 100}%`,
            right: `${sideMarginFrac * 100}%`,
            bottom: `${bottomMarginFrac * 100}%`,
          }}
        />
        {/* Panel body */}
        <div
          role="button"
          tabIndex={0}
          aria-label={STRINGS.book.review.movePanelAriaLabel}
          onPointerDown={handleBodyPointerDown}
          className={
            outlineOnly
              ? 'absolute border-2 border-dashed border-dream-navy bg-transparent cursor-grabbing overflow-hidden'
              : 'absolute border-2 border-dashed border-dream-navy bg-white/90 cursor-grab active:cursor-grabbing overflow-hidden'
          }
          style={{
            left: `${left * 100}%`,
            top: `${top * 100}%`,
            width: `${renderedWidthFrac * 100}%`,
            height: `${height * 100}%`,
            touchAction: 'none',
          }}
        >
          {!outlineOnly && (
            <div
              aria-hidden="true"
              className="absolute text-dream-navy pointer-events-none"
              style={{
                left: `${paddingXFrac * 100}%`,
                right: `${paddingXFrac * 100}%`,
                top: `${paddingYFrac * 100}%`,
                bottom: `${paddingYFrac * 100}%`,
                fontFamily: "'Merriweather', Georgia, serif",
                fontWeight: 400,
                fontSize: `${fontSizePx}px`,
                lineHeight: sceneTextLeadingMult,
                // Fix-6 tail (Sebastian ruling 2026-09-17) — last-line
                // clipping. Backend text-block height math is
                // `(N-1)×leading + fontSize`; browser CSS renders
                // `N×leading` (half-leading above first line + half
                // below last line). Gap ≈ `leading − fontSize` ≈
                // 1.83 mm per panel. When this overlay's `overflow`
                // was `hidden`, the browser's extra bottom half-
                // leading clipped the last line's descender/ink.
                // Removing the clip lets the browser render its
                // natural block; the parent panel body still has
                // `overflow-hidden` with the backend's exact height,
                // so the outline is unchanged. The panel body has
                // 2×padding − (leading − fontSize) ≈ 10.7 mm of
                // room beyond the browser's block, so text stays
                // inside the outline.
                wordBreak: 'normal',
                overflowWrap: 'break-word',
              }}
            >
              {text}
            </div>
          )}
        </div>

        {/* Fix-6 (Sebastian ruling 2026-09-17 item 2). Grips rendered
            as SIBLINGS of the panel body, positioned relative to the
            image container so they cannot be clipped by the panel's
            `overflow: hidden` NOR overlap the text inside. Each grip
            sits centred on the panel's outer edge — half outside the
            panel, half at the edge line — keeping the visible SVG
            entirely outside the text area. 44 px touch target
            preserved. */}
        {resizeSurfaceAvailable && !outlineOnly && (
          <>
            <div
              role="separator"
              aria-label={STRINGS.book.review.resizeHandleLeftAriaLabel}
              aria-orientation="vertical"
              onPointerDown={(e) => beginResize(e, 'left')}
              className="absolute cursor-ew-resize bg-dream-navy text-dream-white rounded-full shadow flex items-center justify-center pointer-events-auto"
              style={{
                width: `${HANDLE_PX}px`,
                height: `${HANDLE_PX}px`,
                left: `calc(${left * 100}% - ${HANDLE_PX / 2}px)`,
                top: `calc(${top * 100}% + ${(height * 100) / 2}% - ${HANDLE_PX / 2}px)`,
                touchAction: 'none',
              }}
            >
              <DoubleArrowSvg />
            </div>
            <div
              role="separator"
              aria-label={STRINGS.book.review.resizeHandleRightAriaLabel}
              aria-orientation="vertical"
              onPointerDown={(e) => beginResize(e, 'right')}
              className="absolute cursor-ew-resize bg-dream-navy text-dream-white rounded-full shadow flex items-center justify-center pointer-events-auto"
              style={{
                width: `${HANDLE_PX}px`,
                height: `${HANDLE_PX}px`,
                left: `calc(${(left + width) * 100}% - ${HANDLE_PX / 2}px)`,
                top: `calc(${top * 100}% + ${(height * 100) / 2}% - ${HANDLE_PX / 2}px)`,
                touchAction: 'none',
              }}
            >
              <DoubleArrowSvg />
            </div>
          </>
        )}
      </div>

      <label className="block text-sm font-semibold text-dream-navy pt-1">
        {STRINGS.book.review.combinedEditorTextLabel}
      </label>
      <textarea
        className="builder-field w-full px-3 py-2 text-base text-dream-navy outline-none resize-none"
        rows={5}
        value={text}
        maxLength={maxCharsScene}
        onChange={(e) => {
          setText(e.target.value);
          setOverflow(false);
          setSaveError(null);
        }}
        aria-label={textEditorAriaLabel}
        autoFocus
      />
      <CharacterCounter
        value={text.length}
        corridor={{ min: CUSTOMER_EDIT_MIN_CHARS, max: maxCharsScene }}
      />

      {overflow && (
        <p role="alert" className="text-sm text-error">
          {STRINGS.book.review.combinedEditorOverflowMessage}
        </p>
      )}
      {saveError !== null && !overflow && (
        <p role="alert" className="text-sm text-error">
          {saveError}
        </p>
      )}

      <div className="flex gap-3 justify-end pt-1">
        <button
          type="button"
          onClick={onClose}
          disabled={save.saving}
          className="h-10 rounded-lg border border-dream-navy/30 px-4 text-sm font-semibold text-dream-navy hover:bg-dream-navy/5 disabled:opacity-40"
        >
          {STRINGS.book.review.movePanelCancel}
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saveDisabled}
          className="h-10 rounded-lg bg-dream-navy px-4 text-sm font-semibold text-dream-white hover:bg-dream-navy/90 disabled:opacity-40"
        >
          {STRINGS.book.review.editorSaveButton}
        </button>
      </div>
    </div>
  );
}

function DoubleArrowSvg() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      width={22}
      height={22}
      fill="none"
      stroke="currentColor"
      strokeWidth={2.4}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M8 8 L4 12 L8 16" />
      <path d="M16 8 L20 12 L16 16" />
    </svg>
  );
}
